import "./magicitem.css";
import React, { useState, useMemo } from "react";
import {
  Container,
  Title,
  Table,
  Loader,
  Overlay,
  Group,
  Button,
  Modal,
  TextInput,
  Textarea,
  Select,
  Badge,
  ActionIcon,
  Pagination,
  Stack,
  Text,
  Center,
} from "@mantine/core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrash, faBook } from "@fortawesome/free-solid-svg-icons";
import useMagicItems from "../../../../hooks/corral/magicItem";

const tierColor = {
  common: "gray",
  uncommon: "green",
  rare: "blue",
  epic: "violet",
  wonderous: "cyan",
  vanguard: "teal",
  legendary: "orange",
  mythic: "red",
  ascendant: "yellow",
  godlike: "pink",
  terminal: "indigo",
  foundational: "dark",
};

const glowStyle = {
  boxShadow: "0 0 8px 2px rgba(255,255,255,0.8)",
  animation: "pulse 2s infinite",
};

const MagicItem = () => {
  const {
    items,
    loading,
    modalOpen,
    tierOptions,
    glyphOptions,
    formValues,
    poolAmount,
    handleInputChange,
    handleSelectChange,
    addItem,
    disenchantItem,
    deleteItem,
    openModal,
    closeModal,
  } = useMagicItems();

  // pagination state
  const [page, setPage] = useState(1);
  const perPage = 10;
  const totalPages = Math.ceil(items.length / perPage);

  // slice items for current page
  const pagedItems = useMemo(
    () => items.slice((page - 1) * perPage, page * perPage),
    [items, page],
  );

  const rows = pagedItems.map((it) => {
    const tier = tierOptions.find((t) => t.value === `${it.tier}`);
    const tierKey = tier?.label?.toLowerCase();
    const isGlowing = parseInt(it.tier) >= 200;
    return (
      <Table.Tr key={it.id}>
        <Table.Td>{it.name}</Table.Td>
        <Table.Td>{it.crafter}</Table.Td>
        <Table.Td>
          <Badge
            color={tierColor[tierKey]}
            style={isGlowing ? glowStyle : undefined}
          >
            {tier.label}
          </Badge>
        </Table.Td>
        <Table.Td>
          {it.glyph && (
            <Badge
              autoContrast
              color={it.glyph.backgroundColor}
              style={{ outline: "1px solid grey" }}
            >
              {it.glyph.displayName}
            </Badge>
          )}
        </Table.Td>
        <Table.Td>
          <ActionIcon
            color="yellow"
            variant="light"
            onClick={() => disenchantItem(it.inventoryId)}
          >
            <FontAwesomeIcon icon={faBook} />
          </ActionIcon>
        </Table.Td>
        <Table.Td>
          <ActionIcon
            color="red"
            variant="light"
            onClick={() => deleteItem(it.inventoryId)}
          >
            <FontAwesomeIcon icon={faTrash} />
          </ActionIcon>
        </Table.Td>
      </Table.Tr>
    );
  });

  return (
    <Container>
      <Group justify="space-between" mb="md">
        <Title order={2}>
          Magic Inventory (Available For Disenchant: {poolAmount})
        </Title>
        {!loading && <Button onClick={openModal}>Add Item</Button>}
      </Group>

      {loading && (
        <Overlay blur={1}>
          <Loader />
        </Overlay>
      )}

      {!loading && (
        <Stack spacing="xs">
          <Table striped highlightOnHover withColumnBorders>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Name</Table.Th>
                <Table.Th>Crafter</Table.Th>
                <Table.Th>Rarity</Table.Th>
                <Table.Th>Foundational Affinity</Table.Th>
                <Table.Th>Disenchant</Table.Th>
                <Table.Th>Delete</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>{rows}</Table.Tbody>
          </Table>

          {totalPages > 1 && (
            <Center>
              <Pagination
                page={page}
                onChange={setPage}
                total={totalPages}
                siblings={1}
                boundaries={1}
                size="sm"
                position="right"
              />
            </Center>
          )}
        </Stack>
      )}

      <Modal
        opened={modalOpen}
        onClose={closeModal}
        title="Add Magic Item"
        size="xl"
        centered
      >
        <TextInput
          name="name"
          label="Name"
          placeholder="Excalibur"
          mb="md"
          required
          value={formValues.name}
          onChange={handleInputChange}
        />
        <TextInput
          name="crafter"
          label="Crafter"
          placeholder="Arthur Wolfe"
          mb="md"
          required
          value={formValues.crafter}
          onChange={handleInputChange}
        />
        <Select
          label="Magic Tier"
          placeholder="Select tier"
          data={tierOptions}
          mb="md"
          searchable
          required
          value={formValues.tier}
          onChange={handleSelectChange("tier")}
        />
        <Select
          label="Glyph Origin"
          placeholder="Choose glyph"
          data={glyphOptions}
          mb="md"
          searchable
          required
          value={formValues.glyphId}
          onChange={handleSelectChange("glyphId")}
        />
        <Textarea
          name="description"
          label="Description"
          placeholder="Item details…"
          autosize
          minRows={3}
          maxRows={15}
          mb="md"
          required
          value={formValues.description}
          onChange={handleInputChange}
        />
        <Group position="right">
          <Button
            onClick={addItem}
            disabled={
              !formValues.name || !formValues.tier || !formValues.glyphId
            }
          >
            Save
          </Button>
        </Group>
      </Modal>
    </Container>
  );
};

export default MagicItem;
